/**
 * Vorlage „Bestreichen“.
 *
 * Ein Backpinsel wird im Schälchen eingetaucht und streicht dann in kurzen
 * Zügen über einen Hefezopf. Wo er war, glänzt die Oberfläche.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path } from '../paths.ts';
import { compact, faded, group, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { brush, place } from '../parts/utensils.ts';
import { bowl, contents } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Blech und Zopf. */
const TRAY_Y = GROUND_Y - 6;
const LOAF = { cx: 136, width: 150 } as const;
const LOAF_LEFT = LOAF.cx - LOAF.width / 2;
const LOAF_RIGHT = LOAF.cx + LOAF.width / 2;
/** Schälchen links. */
const CUP = { cx: 30, rim: GROUND_Y - 20, width: 40, height: 20 } as const;

/** Zeitplan. */
const TIME = {
  appear: [0.02, 0.08],
  dip: [0.08, 0.2],
  toLoaf: [0.2, 0.28],
  brush: [0.28, 0.76],
  away: [0.76, 0.84],
  reset: [0.88, 1],
} as const;

/** Womit bestrichen wird. */
const COATS = { egg: 'Ei', milk: 'Milch', butter: 'Butter' } as const;
type Coat = keyof typeof COATS;
const COAT_TONES: Readonly<Record<Coat, Tone>> = { egg: 'shade', milk: 'snow', butter: 'tint' };

/** Stränge des Zopfs: Mitte, Radien, Drehung. Von hinten nach vorn. */
const STRANDS: readonly { x: number; rx: number; ry: number; turn: number }[] = [
  { x: -58, rx: 16, ry: 12, turn: 20 },
  { x: -34, rx: 19, ry: 15, turn: -24 },
  { x: -8, rx: 20, ry: 17, turn: 24 },
  { x: 18, rx: 20, ry: 17, turn: -24 },
  { x: 42, rx: 18, ry: 14, turn: 22 },
  { x: 62, rx: 14, ry: 11, turn: -18 },
];

/** Oberkante des Zopfs an einer Stelle, für die Pinselspitze. */
function loafTop(x: number): number {
  const f = (x - LOAF.cx) / (LOAF.width / 2);
  return TRAY_Y - 30 + 14 * f * f;
}

/** Zopf; glazed zeigt die bestrichene Fassung mit Glanz. */
function loaf(glazed: boolean): SceneNode[] {
  const nodes: SceneNode[] = [];
  for (const strand of STRANDS) {
    const cx = LOAF.cx + strand.x;
    const cy = TRAY_Y - strand.ry + 1;
    nodes.push(
      group(
        [
          shape(ellipsePath(cx, cy, strand.rx, strand.ry), styles.solid(glazed ? 'shade' : 'tint')),
          glazed
            ? shape(
                path()
                  .move(cx - strand.rx * 0.5, cy - strand.ry * 0.45)
                  .quad(
                    cx - strand.rx * 0.1,
                    cy - strand.ry * 0.75,
                    cx + strand.rx * 0.35,
                    cy - strand.ry * 0.55,
                  )
                  .toString(),
                { stroke: 'paper', strokeWidth: STROKE.detail },
              )
            : shape(
                path()
                  .move(cx - strand.rx * 0.45, cy + strand.ry * 0.1)
                  .quad(cx, cy - strand.ry * 0.25, cx + strand.rx * 0.45, cy + strand.ry * 0.1)
                  .toString(),
                styles.fine('shade'),
              ),
        ],
        { transform: { rotate: strand.turn, ox: cx, oy: cy } },
      ),
    );
  }
  return nodes;
}

/** Vorlage „Bestreichen“. */
export const bakeBrushTemplate = defineTemplate({
  id: 'bake.brush',
  group: 'bake',
  title: 'Bestreichen',
  description:
    'Das Gebäck wird vor dem Backen mit dem Pinsel dünn mit verquirltem Ei, Milch oder Butter bestrichen. So bekommt es Farbe und Glanz.',
  durationSeconds: 6,
  keywords: [
    'bestreichen',
    'einstreichen',
    'einpinseln',
    'bepinseln',
    'mit eigelb',
    'mit ei bestreichen',
    'mit milch bestreichen',
    'mit butter bestreichen',
    'eistreiche',
    'backpinsel',
    'hefezopf',
  ],
  stillTime: 0.52,
  parameters: [choice('coat', 'Bestreichen mit', COATS, 'egg')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/eigelb|\bei\b|eistreiche|verquirlt/.test(lower)) return { coat: 'egg' };
    if (lower.includes('milch')) return { coat: 'milk' };
    if (lower.includes('butter')) return { coat: 'butter' };
    return {};
  },
  scene(values, t) {
    const coat = textValue(values, 'coat', 'egg') as Coat;
    const tone = COAT_TONES[coat] ?? 'shade';
    const cup = bowl(CUP.cx, CUP.rim, CUP.width, CUP.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));

    // Pinsel: herein, eintauchen, zum Zopf, in Zügen über den Zopf, weg.
    const appear = ease.outCubic(segment(t, ...TIME.appear));
    const dip = segment(t, ...TIME.dip);
    const dipDepth = Math.sin(dip * Math.PI) * 14;
    const toLoaf = ease.inOutCubic(segment(t, ...TIME.toLoaf));
    const stroke = segment(t, ...TIME.brush);
    const away = ease.inCubic(segment(t, ...TIME.away));
    const startX = LOAF_LEFT + 10;
    const endX = LOAF_RIGHT - 18;
    const sweepX = lerp(startX, endX, ease.inOutSine(stroke)) + Math.sin(stroke * Math.PI * 10) * 7;
    const cupTip = { x: CUP.cx + 2, y: CUP.rim - 4 + dipDepth };
    const x = stroke > 0 ? sweepX : lerp(cupTip.x, startX, toLoaf);
    const y =
      stroke > 0
        ? loafTop(sweepX) + 2
        : lerp(cupTip.y, loafTop(startX) + 2, toLoaf) - Math.sin(toLoaf * Math.PI) * 26;
    const brushNode = faded(appear * (1 - away), [place(x - away * 14, y - away * 14, 28, brush(), 1.1)]);

    // Der bestrichene Teil wächst mit dem Pinsel und verschwindet am Ende wieder.
    const coatX = (stroke > 0 ? Math.max(startX - 20, sweepX + 10) : startX - 20) + (stroke >= 1 ? 40 : 0);
    const glazeWidth = Math.max(0, coatX - (LOAF_LEFT - 20));
    const glaze =
      stroke > 0
        ? faded(1 - reset, [
            group(loaf(true), {
              clip: path()
                .move(LOAF_LEFT - 20, 0)
                .line(LOAF_LEFT - 20 + glazeWidth, 0)
                .line(LOAF_LEFT - 20 + glazeWidth, GROUND_Y)
                .line(LOAF_LEFT - 20, GROUND_Y)
                .close()
                .toString(),
            }),
          ])
        : null;

    return compact([
      ...counter(),
      shadow(LOAF.cx, LOAF.width * 0.55),
      rect(LOAF_LEFT - 12, TRAY_Y, LOAF.width + 24, 6, 2, styles.solid('ink')),
      ...loaf(false),
      glaze,
      ...cup.back,
      contents(cup, 0.35, tone),
      ...cup.front,
      brushNode,
    ]);
  },
  hints() {
    return [
      arrow([
        { x: LOAF_LEFT + 10, y: loafTop(LOAF_LEFT + 10) - 22 },
        { x: LOAF.cx, y: loafTop(LOAF.cx) - 22 },
        { x: LOAF_RIGHT - 8, y: loafTop(LOAF_RIGHT - 8) - 22 },
      ]),
    ];
  },
});
